import React, { useState } from "react";
import { ExternalLink } from "lucide-react";
import { AppButton, AppCard, AppCardContent, Callout } from "../ui";

/**
 * ZohoMeetingEmbed — the classroom panel for a class hosted on Zoho Meeting.
 *
 * Zoho cannot run inside this page: its join page sends `frame-ancestors`
 * naming only Zoho itself and one partner, so a browser refuses it in an iframe
 * here. Everyone opens the meeting in a new tab, from a click (a popup blocker
 * lets that through), and THIS tab stays open behind it — it sends the
 * heartbeat that counts attendance, and it is where the trainer ends the class.
 *
 * ⚠️ Opening Zoho must never call `onMeetingEnd`. On the trainer's page that
 * handler ends the class for everyone.
 *
 * Props:
 *   joinConfig: { joinUrl, role, passcode?, hostAccount? }
 *     - instructor: joinUrl is Zoho's host start link; hostAccount is the Zoho
 *       account it starts as.
 *   onMeetingEnd: () => void — the student's "I've left the class".
 */
const ZohoMeetingEmbed = ({ joinConfig, onMeetingEnd }) => {
  const [opened, setOpened] = useState(false);

  if (!joinConfig?.joinUrl) {
    return <Callout tone="warning">Meeting link not available yet. Please refresh in a moment.</Callout>;
  }

  const { joinUrl, role, passcode, hostAccount } = joinConfig;
  const isHost = role === "instructor";

  return (
    <AppCard>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <h2 className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">
            {isHost ? "Your class runs on Zoho Meeting" : "Your class is on Zoho Meeting"}
          </h2>
          <p className="tw-m-0 tw-text-sm tw-text-content-muted">
            {isHost
              ? "Zoho Meeting opens in a new tab. Keep this tab open while you teach: it keeps the class live for your students, and you end the class here."
              : "The class opens in Zoho Meeting, in a new tab. Keep this tab open while you are in class: it records your attendance."}
          </p>
        </div>

        {isHost && hostAccount && (
          <p className="tw-m-0 tw-text-sm tw-text-content">
            Sign in to Zoho as <strong>{hostAccount}</strong> to start the meeting as its host.
          </p>
        )}

        {!isHost && passcode && (
          <p className="tw-m-0 tw-text-sm tw-text-content">
            Meeting password: <strong className="tw-font-mono">{passcode}</strong>
          </p>
        )}

        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <AppButton
            as="a"
            href={joinUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant={opened ? "outline" : "default"}
            onClick={() => setOpened(true)}
          >
            <ExternalLink size={16} />
            {opened ? "Open Zoho again" : isHost ? "Start class in Zoho" : "Join class in Zoho"}
          </AppButton>
          {!isHost && opened && (
            <AppButton variant="ghost" onClick={() => onMeetingEnd?.()}>
              I&apos;ve left the class
            </AppButton>
          )}
        </div>

        {opened && (
          <Callout tone="info">
            {isHost
              ? "Zoho Meeting is open in another tab. When the class is over, click End Class at the top of this page."
              : "Zoho Meeting is open in another tab. Keep this tab open until the class ends."}
          </Callout>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default ZohoMeetingEmbed;
